import React, { useState } from "react";
import { supabase } from "../../lib/supabaseClient";
import { useAuth } from "../../context/AuthContext";
import JSZip from "jszip";
import { saveAs } from "file-saver";

/**
 * Bouton d'export COMPLET avec images - Version portable
 * Export ZIP contenant CSV + toutes les images avec nomenclature claire
 * Pour migration vers autre solution ou archivage complet
 */
const ExportButtonComplete = ({ 
  collectionId = null, 
  label = "Export Complet (avec images)", 
  variant = "btn-primary-artsy"
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, status: "" });
  const { user } = useAuth();

  /**
   * Échappe les valeurs pour le CSV
   */
  const escapeCsvValue = (value) => {
    if (value === null || value === undefined) return "";
    const stringValue = String(value);
    if (stringValue.includes(",") || stringValue.includes('"') || stringValue.includes("\n")) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  /**
   * Génère un nom de fichier sécurisé et lisible
   */
  const sanitizeFilename = (str) => {
    return str
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") // Enlever accents
      .replace(/[^a-z0-9]/gi, "_") // Remplacer caractères spéciaux
      .replace(/_+/g, "_") // Éviter double underscore
      .toLowerCase()
      .substring(0, 50); // Limiter longueur
  };

  /**
   * Parse les détails d'acquisition
   */
  const parseAcquisitionDetails = (acquisitionDetails) => {
    if (!acquisitionDetails) return { type: "", price: "", date: "", source: "" };
    try {
      const parsed = typeof acquisitionDetails === "string" 
        ? JSON.parse(acquisitionDetails) 
        : acquisitionDetails;
      return {
        type: parsed.type || parsed.acquisition_type || "",
        price: parsed.price || parsed.acquisition_price || "",
        date: parsed.acquisition_date || parsed.date || "",
        source: parsed.acquisition_source || parsed.source || ""
      };
    } catch (e) {
      return { type: "", price: "", date: "", source: "" };
    }
  };

  /**
   * Parse les images
   */
  const parseImages = (images) => {
    if (!images) return [];
    try {
      const parsed = typeof images === "string" ? JSON.parse(images) : images;
      if (Array.isArray(parsed)) {
        return parsed.map((img, idx) => ({
          url: img.url || img.path || "",
          isPrimary: img.isPrimary || idx === 0,
          id: img.id || `img_${idx}`
        }));
      }
    } catch (e) {
      console.warn("Impossible de parser images:", e);
    }
    return [];
  };

  /**
   * Parse les dimensions
   */
  const parseDimensions = (specifications, dimensions) => {
    let dims = { height: "", width: "", depth: "", unit: "cm" };
    try {
      if (specifications) {
        const parsed = typeof specifications === "string" 
          ? JSON.parse(specifications) 
          : specifications;
        if (parsed.dimensions) {
          dims = {
            height: parsed.dimensions.height || "",
            width: parsed.dimensions.width || "",
            depth: parsed.dimensions.depth || "",
            unit: parsed.dimensions.unit || "cm"
          };
        }
      }
    } catch (e) {
      if (dimensions) dims.display = dimensions;
    }
    return dims;
  };

  /**
   * Formate les dimensions
   */
  const formatDimensions = (dims) => {
    const parts = [];
    if (dims.height) parts.push(`H ${dims.height}`);
    if (dims.width) parts.push(`L ${dims.width}`);
    if (dims.depth) parts.push(`P ${dims.depth}`);
    if (parts.length === 0) return "";
    return parts.join(" × ") + (dims.unit ? ` ${dims.unit}` : "");
  };

  /**
   * Formate une date
   */
  const formatDate = (date) => {
    if (!date) return "";
    try {
      return new Date(date).toLocaleDateString("fr-FR");
    } catch (e) {
      return date;
    }
  };

  /**
   * Télécharge une image et retourne le blob
   */
  const downloadImage = async (url) => {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error("Impossible de télécharger l'image");
      return await response.blob();
    } catch (error) {
      console.error("Erreur téléchargement image:", url, error);
      return null;
    }
  };

  /**
   * Génère le fichier README.txt pour le ZIP
   */
  const generateReadme = (collections, totalArtworks, totalImages) => {
    const date = new Date().toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });

    return `
═══════════════════════════════════════════════════════════════════
EXPORT COMPLET ARTKIV - PORTABILITÉ DES DONNÉES
═══════════════════════════════════════════════════════════════════

Date d'export : ${date}
Client : ${user.email || ""}
Nombre de collections : ${collections.length}
Nombre total d'œuvres : ${totalArtworks}
Nombre total d'images : ${totalImages}

═══════════════════════════════════════════════════════════════════
STRUCTURE DU FICHIER
═══════════════════════════════════════════════════════════════════

📁 export_artkiv_YYYY-MM-DD.zip
├── 📄 README.txt (ce fichier)
├── 📄 index.csv (index complet de toutes les données)
├── 📁 images/
│   ├── 📁 collection_001_nom/
│   │   ├── 🖼️ oeuvre_001_titre_img_1.jpg
│   │   ├── 🖼️ oeuvre_001_titre_img_2.jpg
│   │   └── 🖼️ oeuvre_002_titre_img_1.jpg
│   └── 📁 collection_002_nom/
│       └── 🖼️ oeuvre_003_titre_img_1.jpg

═══════════════════════════════════════════════════════════════════
NOMENCLATURE DES FICHIERS IMAGES
═══════════════════════════════════════════════════════════════════

Format : oeuvre_XXX_titre_img_Y.ext

- XXX : Numéro d'œuvre (3 chiffres)
- titre : Titre de l'œuvre (simplifié, sans accents)
- Y : Numéro de l'image (1 = image principale)
- ext : Extension (jpg, png, etc.)

Exemple : oeuvre_001_les_nympheas_img_1.jpg

═══════════════════════════════════════════════════════════════════
FICHIER INDEX.CSV
═══════════════════════════════════════════════════════════════════

Le fichier index.csv contient TOUTES les informations de vos œuvres :
- Informations œuvre (titre, artiste, année, dimensions, etc.)
- Informations collection (nom de la collection)
- Valeurs (estimée, achat, assurance)
- Acquisition (date, source, type)
- Chemins vers les fichiers images dans le ZIP

Ce fichier peut être importé dans n'importe quel logiciel de gestion
d'œuvres d'art ou base de données.

═══════════════════════════════════════════════════════════════════
UTILISATION
═══════════════════════════════════════════════════════════════════

1. MIGRATION VERS AUTRE SOLUTION
   - Importez le fichier index.csv dans votre nouveau logiciel
   - Les chemins d'images sont relatifs et portables
   - Toutes vos données sont préservées

2. ARCHIVAGE
   - Conservez ce ZIP comme sauvegarde complète
   - Toutes les images haute résolution sont incluses
   - Aucune dépendance externe

3. PARTAGE AVEC EXPERT/ASSURANCE
   - Envoyez le ZIP complet
   - L'expert a accès à toutes les informations
   - Format standard universellement compatible

═══════════════════════════════════════════════════════════════════
CONFORMITÉ RGPD
═══════════════════════════════════════════════════════════════════

Cet export répond à l'obligation de portabilité des données 
(Article 20 du RGPD). Vous pouvez :

✅ Migrer vers toute autre solution
✅ Conserver vos données indépendamment d'ArtKiv
✅ Partager vos données avec des tiers
✅ Archiver pour conservation légale

═══════════════════════════════════════════════════════════════════
SUPPORT TECHNIQUE
═══════════════════════════════════════════════════════════════════

Questions sur l'export ? contact@artkiv.com

Format CSV non reconnu ? Ouvrez avec :
- Microsoft Excel (Données > Importer > UTF-8)
- Google Sheets (Fichier > Importer > UTF-8)
- LibreOffice Calc (Encodage : UTF-8)

═══════════════════════════════════════════════════════════════════

Merci d'avoir utilisé ArtKiv !
Vos données vous appartiennent. 🎨
`.trim();
  };

  /**
   * Génère le CSV d'index pour le ZIP
   */
  const generateIndexCsv = (collections, artworksMap) => {
    const lines = [];
    
    // En-tête
    lines.push("=== EXPORT COMPLET ARTKIV ===");
    lines.push(`Date d'export,${formatDate(new Date())}`);
    lines.push(`Client,${user.email || ""}`);
    lines.push("");

    // En-tête du tableau
    const headers = [
      "ID Œuvre",
      "Numéro Œuvre",
      "Collection",
      "Titre",
      "Artiste",
      "Année",
      "Medium/Technique",
      "Dimensions",
      "Valeur estimée (€)",
      "Prix d'achat (€)",
      "Date d'acquisition",
      "Source d'acquisition",
      "Type d'acquisition",
      "État",
      "Signature",
      "Édition",
      "Description",
      "Nombre d'images",
      "Chemin Image 1",
      "Chemin Image 2",
      "Chemin Image 3",
      "Chemin Image 4",
      "Chemin Image 5"
    ];

    lines.push(headers.map(h => escapeCsvValue(h)).join(","));

    // Données
    let artworkNumber = 1;
    collections.forEach(collection => {
      const artworks = artworksMap[collection.id] || [];
      const collectionName = sanitizeFilename(collection.title || "sans_nom");

      artworks.forEach(artwork => {
        const acquisition = parseAcquisitionDetails(artwork.acquisitionDetails);
        const dims = parseDimensions(artwork.specifications, artwork.dimensions);
        const images = parseImages(artwork.images);
        const artworkTitle = sanitizeFilename(artwork.title || "sans_titre");
        
        const paddedNumber = String(artworkNumber).padStart(3, "0");
        
        const row = [
          artwork.id,
          paddedNumber,
          collection.title || "",
          artwork.title || "Sans titre",
          artwork.artist || "",
          artwork.year || artwork.creationDate || "",
          artwork.medium || artwork.typeOfArt || "",
          formatDimensions(dims),
          artwork.value || "",
          acquisition.price,
          formatDate(acquisition.date),
          acquisition.source,
          acquisition.type,
          artwork.condition || "",
          artwork.signature_desc || artwork.signature || "",
          artwork.isEdition === "true" || artwork.isEdition === true 
            ? (artwork.editionInfo || "Oui") 
            : "",
          artwork.description || "",
          images.length
        ];

        // Ajouter les chemins d'images (jusqu'à 5)
        for (let i = 0; i < 5; i++) {
          if (i < images.length) {
            const ext = images[i].url.split(".").pop().toLowerCase();
            const imagePath = `images/${collectionName}/oeuvre_${paddedNumber}_${artworkTitle}_img_${i + 1}.${ext}`;
            row.push(imagePath);
          } else {
            row.push("");
          }
        }

        lines.push(row.map(v => escapeCsvValue(v)).join(","));
        artworkNumber++;
      });
    });

    return lines.join("\n");
  };

  /**
   * Export complet avec ZIP
   */
  const exportCompleteWithImages = async () => {
    setIsExporting(true);
    setProgress({ current: 0, total: 0, status: "Préparation..." });

    try {
      // 1. Récupérer les collections
      setProgress({ current: 0, total: 0, status: "Récupération des collections..." });
      
      let collectionsQuery = supabase
        .from("collections")
        .select("*")
        .eq("client_id", user.id);

      if (collectionId) {
        collectionsQuery = collectionsQuery.eq("id", collectionId);
      }

      const { data: collections, error: collectionsError } = await collectionsQuery.order("title");
      
      if (collectionsError) throw collectionsError;
      if (!collections || collections.length === 0) {
        alert("Aucune collection à exporter");
        return;
      }

      // 2. Récupérer les œuvres
      setProgress({ current: 0, total: 0, status: "Récupération des œuvres..." });
      
      let artworksQuery = supabase
        .from("artworks")
        .select("*")
        .eq("client_id", user.id);

      if (collectionId) {
        artworksQuery = artworksQuery.eq("collection_id", collectionId);
      }

      const { data: allArtworks, error: artworksError } = await artworksQuery;
      
      if (artworksError) throw artworksError;

      // Organiser les œuvres par collection
      const artworksMap = {};
      collections.forEach(col => {
        artworksMap[col.id] = allArtworks.filter(art => art.collection_id === col.id);
      });

      // 3. Compter les images totales
      let totalImages = 0;
      allArtworks.forEach(artwork => {
        const images = parseImages(artwork.images);
        totalImages += images.length;
      });

      setProgress({ 
        current: 0, 
        total: totalImages, 
        status: `Téléchargement de ${totalImages} images...` 
      });

      // 4. Créer le ZIP
      const zip = new JSZip();
      
      // Ajouter le README
      const readme = generateReadme(collections, allArtworks.length, totalImages);
      zip.file("README.txt", readme);

      // Ajouter le CSV d'index
      const indexCsv = generateIndexCsv(collections, artworksMap);
      zip.file("index.csv", "\ufeff" + indexCsv); // BOM pour UTF-8

      // 5. Télécharger et ajouter les images
      const imagesFolder = zip.folder("images");
      let currentImage = 0;
      let artworkNumber = 1;

      for (const collection of collections) {
        const collectionName = sanitizeFilename(collection.title || "sans_nom");
        const collectionFolder = imagesFolder.folder(collectionName);
        const artworks = artworksMap[collection.id] || [];

        for (const artwork of artworks) {
          const images = parseImages(artwork.images);
          const artworkTitle = sanitizeFilename(artwork.title || "sans_titre");
          const paddedNumber = String(artworkNumber).padStart(3, "0");

          for (let i = 0; i < images.length; i++) {
            const image = images[i];
            if (!image.url) continue;

            currentImage++;
            setProgress({
              current: currentImage,
              total: totalImages,
              status: `Téléchargement image ${currentImage}/${totalImages}...`
            });

            try {
              const blob = await downloadImage(image.url);
              if (blob) {
                const ext = image.url.split(".").pop().toLowerCase().split("?")[0];
                const filename = `oeuvre_${paddedNumber}_${artworkTitle}_img_${i + 1}.${ext}`;
                collectionFolder.file(filename, blob);
              }
            } catch (error) {
              console.error(`Erreur téléchargement image ${image.url}:`, error);
              // Continuer même si une image échoue
            }
          }

          artworkNumber++;
        }
      }

      // 6. Générer et télécharger le ZIP
      setProgress({ current: totalImages, total: totalImages, status: "Génération du ZIP..." });
      
      const zipBlob = await zip.generateAsync({ 
        type: "blob",
        compression: "DEFLATE",
        compressionOptions: { level: 6 }
      }, (metadata) => {
        setProgress({
          current: totalImages,
          total: totalImages,
          status: `Compression... ${metadata.percent.toFixed(0)}%`
        });
      });

      // 7. Télécharger
      const filename = collectionId
        ? `artkiv_export_complet_${sanitizeFilename(collections[0].title)}_${new Date().toISOString().split("T")[0]}.zip`
        : `artkiv_export_complet_${new Date().toISOString().split("T")[0]}.zip`;
      
      saveAs(zipBlob, filename);

      setProgress({ 
        current: totalImages, 
        total: totalImages, 
        status: "✅ Export terminé !" 
      });

      // Réinitialiser après 3 secondes
      setTimeout(() => {
        setProgress({ current: 0, total: 0, status: "" });
        setIsExporting(false);
      }, 3000);

    } catch (error) {
      console.error("Erreur lors de l'export complet:", error);
      alert("Une erreur est survenue lors de l'export. Veuillez réessayer.");
      setIsExporting(false);
      setProgress({ current: 0, total: 0, status: "" });
    }
  };

  /**
   * Gère le clic
   */
  const handleExport = () => {
    if (!user) {
      alert("Vous devez être connecté pour exporter");
      return;
    }

    const confirm = window.confirm(
      collectionId
        ? "Cet export peut prendre plusieurs minutes selon le nombre d'images.\n\nVoulez-vous continuer ?"
        : "ATTENTION : Cet export va télécharger TOUTES vos images.\nCela peut prendre 10-30 minutes selon la taille de votre collection.\n\nVoulez-vous continuer ?"
    );

    if (confirm) {
      exportCompleteWithImages();
    }
  };

  return (
    <div className="relative">
      <button
        onClick={handleExport}
        disabled={isExporting}
        className={`btn-artsy ${variant} ${
          isExporting ? "opacity-50 cursor-not-allowed" : ""
        }`}
        title="Export complet avec images (ZIP) pour portabilité / migration"
      >
        {isExporting ? (
          <>
            <svg
              className="animate-spin -ml-1 mr-2 h-4 w-4 inline"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            {progress.current > 0 && progress.total > 0
              ? `${progress.current}/${progress.total}`
              : "Export..."}
          </>
        ) : (
          <>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-4 h-4 inline mr-2 -mt-0.5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z"
              />
            </svg>
            {label}
          </>
        )}
      </button>

      {/* Barre de progression */}
      {isExporting && progress.status && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-300 rounded-md p-3 shadow-lg z-50 min-w-max">
          <div className="text-xs text-gray-700 mb-2">{progress.status}</div>
          {progress.total > 0 && (
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-black h-2 rounded-full transition-all duration-300"
                style={{
                  width: `${Math.min(100, (progress.current / progress.total) * 100)}%`
                }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ExportButtonComplete;